/**
 * Application Constants
 * 
 * Central location for all magic strings, numbers, and configuration values
 * used throughout the application.
 */

// ==================== Status Constants ====================

export const TRAINING_SET_STATUS = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const

export const TRAINING_PROJECTION_STATUS = {
  SCHEDULED: 'SCHEDULED',
  COMPLETED: 'COMPLETED',
  MOVED: 'MOVED',
  SKIPPED: 'SKIPPED',
  CANCELLED: 'CANCELLED',
  ACTIVE: 'ACTIVE',
} as const

export const USER_RELATION_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING_APPROVAL_BY_TRAINEE: 'PENDING_APPROVAL_BY_TRAINEE',
  PENDING_APPROVAL_BY_COACH: 'PENDING_APPROVAL_BY_COACH',
  DECLINED_BY_TRAINEE: 'DECLINED_BY_TRAINEE',
  DECLINED_BY_COACH: 'DECLINED_BY_COACH',
  INACTIVE: 'INACTIVE',
} as const

// ==================== UI Constants ====================

export const THEME_COLORS = {
  primary: {
    bg: 'bg-blue-500/15',
    border: 'border-blue-500/30',
    hover: 'hover:bg-blue-500/25',
  },
  success: {
    bg: 'bg-green-500/20',
    border: 'border-green-500/30',
    text: 'text-green-300',
    hover: 'hover:bg-green-500/30',
  },
  error: {
    bg: 'bg-red-500/20',
    border: 'border-red-500/30',
    text: 'text-red-300',
    hover: 'hover:bg-red-500/30',
  },
  warning: {
    bg: 'bg-yellow-500/20',
    border: 'border-yellow-500/30',
    text: 'text-yellow-300',
    hover: 'hover:bg-yellow-500/30',
  },
  info: {
    bg: 'bg-sky-500/15',
    border: 'border-sky-500/30',
    hover: 'hover:bg-sky-500/25',
  },
  neutral: {
    bg: 'bg-white/10',
    border: 'border-gray-400/20',
    hover: 'hover:bg-white/15',
  },
} as const

export const TRANSITION_CLASSES = 'transition-all active:scale-[0.98]'
export const CARD_HOVER_CLASSES = 'hover:translate-x-1 active:translate-x-0.5'

// ==================== Date & Time Constants ====================

export const DATE_FORMAT_OPTIONS = {
  short: { month: 'short', day: 'numeric' } as const,
  long: { weekday: 'long', month: 'short', day: 'numeric' } as const,
  full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' } as const,
}

export const TIME_FORMAT_OPTIONS = {
  short: { hour: '2-digit', minute: '2-digit' } as const,
  full: { hour: '2-digit', minute: '2-digit', second: '2-digit' } as const,
  hour24: { hour: '2-digit', minute: '2-digit', hour12: false } as const,
}

// ==================== Days of Week ====================

export const DAYS_OF_WEEK = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
] as const

// ==================== Validation Constants ====================

export const VALIDATION = {
  MIN_TRAININGS: 1,
  MAX_TRAININGS: 100,
  MIN_DURATION_MINUTES: 15,
  MAX_DURATION_HOURS: 24,
  DEFAULT_TRAININGS: 12,
  DEFAULT_DURATION: { hours: 1, minutes: 30 },
} as const

// ==================== Messages ====================

export const MESSAGES = {
  errors: {
    loadingFailed: 'Failed to load data',
    saveFailed: 'Failed to save changes',
    deleteFailed: 'Failed to delete',
    networkError: 'Network error. Please check your connection.',
    unauthorized: 'Unauthorized. Please login again.',
  },
  success: {
    saved: 'Changes saved successfully',
    deleted: 'Deleted successfully',
    completed: 'Completed successfully',
  },
  confirmations: {
    delete: 'Are you sure you want to delete? This action cannot be undone.',
    complete: 'Are you sure you want to complete this?',
    cancel: 'Are you sure you want to cancel?',
  },
  warnings: {
    activeSetExists: 'This trainee already has an active training set. Please complete or cancel the existing set before creating a new one.',
    inactiveRelation: 'Training sets can only be created for trainees with an ACTIVE relationship status.',
    unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
  },
} as const

// ==================== Icons & Emojis ====================

export const ICONS = {
  loading: '⏳',
  error: '❌',
  success: '✅',
  warning: '⚠️',
  empty: '📝',
  fire: '🔥',
  calendar: '📅',
  stats: '📊',
  people: '👥',
  add: '➕',
  delete: '🗑️',
  edit: '✏️',
  back: '←',
  forward: '→',
  coach: '👨‍🏫',
  trainee: '🏃',
} as const

// ==================== Filter Options ====================

export const TRAINEE_FILTERS = [
  { value: 'ALL', label: 'All' },
  { value: USER_RELATION_STATUS.ACTIVE, label: 'Active' },
  { value: USER_RELATION_STATUS.PENDING_APPROVAL_BY_TRAINEE, label: 'Pending Trainee' },
  { value: USER_RELATION_STATUS.DECLINED_BY_TRAINEE, label: 'Declined by Trainee' },
] as const

// ==================== Type Exports ====================

export type TrainingSetStatus = typeof TRAINING_SET_STATUS[keyof typeof TRAINING_SET_STATUS]
export type TrainingProjectionStatus = typeof TRAINING_PROJECTION_STATUS[keyof typeof TRAINING_PROJECTION_STATUS]
export type UserRelationStatus = typeof USER_RELATION_STATUS[keyof typeof USER_RELATION_STATUS]
